import React, { useState, useEffect } from 'react';
import { useQueue } from '../contexts/QueueContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { buildDiagnosticReport, shareDiagnosticReport } from '../services/diagnosticExport';

/**
 * SyncScreen - Dedizierter Synchronisations-Screen
 * 
 * Features:
 * - Queue-Liste mit Details (Dateiname, Projekt, Phase, Status)
 * - Live-Progress "X von Y synchronisiert"
 * - Geschätzte Restzeit (ETA)
 * - Priorisierung: Pflicht-Dokumente zuerst
 * - Aktionen: Pause/Resume, Retry
 * - Status pro Item: waiting | syncing | success | error
 */

interface QueueItemWithDetails {
  id: string;
  fileName: string;
  projectName?: string;
  phaseName?: string;
  status: 'waiting' | 'syncing' | 'success' | 'error';
  required?: boolean;
  timestamp: Date;
  fileSize?: number;
}

const getItemDisplayName = (item: { type: string; data: any }): string => {
  const d = item.data || {};
  switch (item.type) {
    case 'document_upload':
      return d.dealName ? `Dokument · ${d.dealName}` : d.documentId || 'Dokument-Upload';
    case 'make_pdf_upload':
      return d.documentTitle || d.filename || 'PDF-Upload';
    case 'projektdokumentation_upsert':
      return `Projektdokumentation${d.projectId ? ` · ${d.projectId}` : ''}`;
    case 'projektdokumentation_status_update':
      return `Projektdokumentation Status${d.projectId ? ` · ${d.projectId}` : ''}`;
    case 'abschlussbericht_submit':
      return `Abschlussbericht${d.projectId ? ` · ${d.projectId}` : ''}`;
    case 'abschlussbericht_draft_upsert':
      return `Abschlussbericht (Entwurf)${d.projectId ? ` · ${d.projectId}` : ''}`;
    case 'checklist_update':
      return 'Checkliste';
    case 'image_upload':
      return d.fileName || d.filename || 'Bild-Upload';
    default:
      return d.fileName || d.filename || d.documentTitle || item.type;
  }
};

const SyncScreen: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showToast } = useToast();
  const { items, syncStatus, syncQueue, isProcessing, isOnline, removeFromQueue } = useQueue();
  const [estimatedTime, setEstimatedTime] = useState<string>('');

  // ADO #1231 (Kriterium 7): erlaubt einem Nutzer mit Upload-Problemen, Queue-Zustand + letzte
  // Konsolen-Fehler + Geräte-Infos direkt an den Support zu schicken, statt dass Support auf
  // physischen Gerätezugriff angewiesen ist (Ticket-Kommentar des Erstellers).
  const handleExportDiagnostics = async () => {
    try {
      const report = buildDiagnosticReport(items);
      const outcome = await shareDiagnosticReport(report);
      if (outcome === 'copied') {
        showToast('success', 'Diagnose-Report in die Zwischenablage kopiert', 4000);
      } else if (outcome === 'shared') {
        showToast('success', 'Diagnose-Report geteilt', 3000);
      }
    } catch (error) {
      console.error('[SyncScreen] Diagnose-Export fehlgeschlagen:', error);
      showToast('error', 'Diagnose-Export fehlgeschlagen', 4000);
    }
  };

  // ETA berechnen (3 Sekunden pro Upload im Durchschnitt)
  useEffect(() => {
    if (syncStatus.pending > 0 && isOnline) {
      const avgUploadTime = 3; // Sekunden
      const remainingSeconds = syncStatus.pending * avgUploadTime;
      
      if (remainingSeconds < 60) {
        setEstimatedTime(`~${remainingSeconds} Sekunden`);
      } else if (remainingSeconds < 3600) {
        const minutes = Math.ceil(remainingSeconds / 60);
        setEstimatedTime(`~${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`);
      } else {
        const hours = Math.floor(remainingSeconds / 3600);
        const minutes = Math.ceil((remainingSeconds % 3600) / 60);
        setEstimatedTime(`~${hours}h ${minutes}m`);
      }
    } else {
      setEstimatedTime('');
    }
  }, [syncStatus.pending, isOnline]);

  // Status aus echtem isProcessing/retries ableiten statt hartcodiert 'waiting' (ADO #1231,
  // Kriterium 2/3): `isProcessing` gilt für den gesamten laufenden Batch (processQueue
  // verarbeitet alle Items parallel, kein granularer Pro-Item-Fortschritt), ein Item mit
  // bereits mindestens einem gescheiterten Versuch (`retries > 0`) zeigt 'error', bis der
  // nächste Sync-Durchlauf es entweder entfernt (Erfolg) oder erneut inkrementiert.
  const queueItemsWithDetails: QueueItemWithDetails[] = items.map(item => ({
    id: item.id,
    fileName: getItemDisplayName(item),
    projectName: item.data?.projectName,
    phaseName: item.data?.phaseName,
    status: isProcessing ? 'syncing' : item.retries > 0 ? 'error' : 'waiting',
    required: item.data?.required,
    timestamp: new Date(item.createdAt),
    fileSize: item.data?.fileSize
  }));

  // Sortierung: Pflicht zuerst, dann nach Timestamp
  const sortedItems = [...queueItemsWithDetails].sort((a, b) => {
    if (a.required && !b.required) return -1;
    if (!a.required && b.required) return 1;
    return a.timestamp.getTime() - b.timestamp.getTime();
  });

  // Pflicht und Optional trennen
  const requiredItems = sortedItems.filter(item => item.required);
  const optionalItems = sortedItems.filter(item => !item.required);

  // Fortschrittsberechnung
  const totalItems = syncStatus.lastResult 
    ? syncStatus.lastResult.successful + syncStatus.lastResult.failed + syncStatus.pending
    : syncStatus.pending;
  const completedItems = syncStatus.lastResult?.successful || 0;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const navigateToProjectsOverview = () => {
    navigate('/projects', { replace: true });
  };

  // Status-Icon Helper
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'syncing':
        return (
          <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default: // waiting
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Dateigröße formatieren
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      <AppHeader
        title={(
          <span className="flex items-center gap-2">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Synchronisierung
          </span>
        )}
        backAction={{ onClick: navigateToProjectsOverview }}
        containerClassName="mx-auto max-w-screen-xl px-4 py-4"
        menuContent={(
          <>
            <button
              onClick={handleExportDiagnostics}
              data-testid="export-diagnostics"
              className="mt-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-gray-700 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M4 4h16v10a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
              </svg>
              Diagnose exportieren
            </button>
            <button
              onClick={logout}
              className="mt-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </>
        )}
      />

      <div className="max-w-screen-xl mx-auto px-4 py-6">
        {/* Progress-Section */}
        {syncStatus.pending > 0 || syncStatus.lastResult ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isProcessing ? 'Wird synchronisiert' : syncStatus.pending > 0 ? 'Bereit zum Synchronisieren' : 'Synchronisierung abgeschlossen'}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {completedItems} von {totalItems}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${
                    syncStatus.state === 'failed' 
                      ? 'bg-red-600' 
                      : syncStatus.state === 'completed'
                      ? 'bg-green-600'
                      : 'bg-blue-600'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* ETA */}
            {estimatedTime && syncStatus.pending > 0 && isOnline && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Fertig in {estimatedTime}
              </p>
            )}

            {/* Offline-Warning */}
            {!isOnline && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Keine Internetverbindung – Uploads werden automatisch synchronisiert, sobald wieder online
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Empty State */
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Alles synchronisiert
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Keine ausstehenden Uploads in der Warteschlange
            </p>
            {syncStatus.lastSync && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                Zuletzt synchronisiert: {new Date(syncStatus.lastSync).toLocaleString('de-DE')}
              </p>
            )}
          </div>
        )}

        {/* Pflicht-Dokumente Section */}
        {requiredItems.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3 flex items-center">
              <span className="w-2 h-2 bg-red-600 rounded-full mr-2"></span>
              Pflicht-Dokumente (Priorität)
            </h2>
            <div className="space-y-2">
              {requiredItems.map(item => (
                <div
                  key={item.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-l-4 border-red-600"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        {getStatusIcon(item.status)}
                        <h3 className="ml-2 font-medium text-gray-900 dark:text-white">
                          {item.fileName}
                        </h3>
                      </div>
                      {(item.projectName || item.phaseName) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 ml-7">
                          {item.projectName} {item.phaseName && `· ${item.phaseName}`}
                        </p>
                      )}
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-500 mt-1 ml-7">
                        <span>{new Date(item.timestamp).toLocaleString('de-DE')}</span>
                        {item.fileSize && (
                          <>
                            <span className="mx-2">·</span>
                            <span>{formatFileSize(item.fileSize)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // Code-Review-Finding zu #1199: removeQueueItem kann jetzt bei einem
                        // Persistenzfehler werfen - ohne dieses .catch() wäre das eine
                        // unhandled promise rejection.
                        removeFromQueue(item.id).catch((error: unknown) => {
                          console.error('Queue-Item konnte nicht entfernt werden:', error);
                        });
                      }}
                      disabled={isProcessing}
                      className="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Aus Warteschlange entfernen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Optionale Dokumente Section */}
        {optionalItems.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
              Optionale Dokumente
            </h2>
            <div className="space-y-2">
              {optionalItems.map(item => (
                <div
                  key={item.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        {getStatusIcon(item.status)}
                        <h3 className="ml-2 font-medium text-gray-900 dark:text-white">
                          {item.fileName}
                        </h3>
                      </div>
                      {(item.projectName || item.phaseName) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 ml-7">
                          {item.projectName} {item.phaseName && `· ${item.phaseName}`}
                        </p>
                      )}
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-500 mt-1 ml-7">
                        <span>{new Date(item.timestamp).toLocaleString('de-DE')}</span>
                        {item.fileSize && (
                          <>
                            <span className="mx-2">·</span>
                            <span>{formatFileSize(item.fileSize)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // Code-Review-Finding zu #1199: removeQueueItem kann jetzt bei einem
                        // Persistenzfehler werfen - ohne dieses .catch() wäre das eine
                        // unhandled promise rejection.
                        removeFromQueue(item.id).catch((error: unknown) => {
                          console.error('Queue-Item konnte nicht entfernt werden:', error);
                        });
                      }}
                      disabled={isProcessing}
                      className="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Aus Warteschlange entfernen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {syncStatus.pending > 0 && isOnline && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <button
              onClick={syncQueue}
              disabled={isProcessing}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
                isProcessing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              }`}
              data-testid="sync-now-button"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Synchronisiere...
                </span>
              ) : (
                `Jetzt ${syncStatus.pending} ${syncStatus.pending === 1 ? 'Datei' : 'Dateien'} synchronisieren`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncScreen;
